import { Fragment, useRef, useState } from 'react';
import {
  AccountBalanceOutlined,
  Add,
  ArrowDropDown,
  Check,
  Link,
  LinkOff,
} from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Popper,
  styled,
  Typography,
} from '@mui/material';
import { Row, Column } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Avatar from 'further-ui/components/Avatar';

const selectorWidth = '400px';

const BankAccountSelectStyled = styled(MenuList)(({ theme }) => ({
  maxWidth: selectorWidth,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(theme.spacingSizes.sm),
  gap: theme.spacing(theme.spacingSizes.xxs),
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const BankAccountSelectButton = styled(ButtonBase)(({ theme }) => ({
  width: '100%',
  maxWidth: selectorWidth,
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.sm),
  borderRadius: theme.shape.borderRadius,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const MenuItemStyled = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'borderless',
})<{ borderless?: boolean }>(({ theme, borderless }) => ({
  border: borderless ? 'none' : `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.sm),
  borderRadius: theme.shape.borderRadius,
}));

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: selectorWidth,
  flexGrow: 1,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: theme.zIndex.modal,
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  maxWidth: selectorWidth,
  width: '100%',
  borderRadius: 0,
  border: 'none',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const Account: React.FC<{
  selected?: boolean;
  accountName: string;
  iban?: string | null;
  accountNumber?: string;
  bankFeedAccountId?: string | null;
  bankFeedInstitutionLogo?: string;
  accountCurrency?: CurrencyCode;
}> = ({
  accountName,
  iban,
  bankFeedAccountId,
  bankFeedInstitutionLogo,
  selected,
  accountNumber,
  accountCurrency,
}) => (
  <Row fullWidth centered justify="space-between">
    <Row centered spacing="sm" fullWidth>
      <Row centered spacing="xs">
        <Avatar
          src={bankFeedInstitutionLogo}
          colorKey={{ accountName }}
          icon={AccountBalanceOutlined}
          size="lg"
        />
        <Column fullWidth>
          <Row centered spacing="sm" justify="space-between">
            <Typography variant="h6">
              <Row spacing="xs" centered>
                {accountName}
                {accountCurrency ? ` (${accountCurrency})` : null}
                {bankFeedAccountId ? (
                  <Link fontSize="small" color="success" />
                ) : (
                  <LinkOff fontSize="small" color="error" />
                )}
              </Row>
            </Typography>
          </Row>
          <Typography textAlign="left" variant="body2">
            {iban
              ? `IBAN ${iban}`
              : accountNumber
              ? `Account number ${accountNumber}`
              : null}
          </Typography>
        </Column>
      </Row>
    </Row>
    {selected ? <Check /> : null}
  </Row>
);

const AddAccount: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => (
  <MenuItemStyled onClick={onClick}>
    <Row spacing="sm" justify="space-between" fullWidth centered>
      <Typography variant="h6">Add a new bank account</Typography>
      <Add />
    </Row>
  </MenuItemStyled>
);

type Props = {
  selectedBankAccountId?: string;
  bankAccounts: Array<{
    id: string;
    bankName: string;
    iban?: string | null;
    accountNumber?: string;
    accountName: string;
    bankFeedAccountId?: string | null;
    bankFeedInstitutionLogo?: string;
    accountCurrency?: CurrencyCode;
  }>;
  onBankAccountChange: (bankAccountId: string) => void;
  onBankAccountAdd: (useFeed: boolean) => void;
};

const menuId = 'bank-account-select-menu';

const BankAccountSelect: React.FC<Props> = ({
  bankAccounts,
  onBankAccountAdd,
  onBankAccountChange,
  selectedBankAccountId,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleMenuItemClick = (accountId: string) => {
    onBankAccountChange(accountId);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const selectedBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.id === selectedBankAccountId,
  );

  return (
    <Container>
      <BankAccountSelectButton
        id={menuId}
        onClick={handleToggle}
        ref={anchorRef}
      >
        <Row centered fullWidth justify="space-between">
          {selectedBankAccount ? <Account {...selectedBankAccount} /> : null}
          <ArrowDropDown />
        </Row>
      </BankAccountSelectButton>
      <StyledPopper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement="bottom-start"
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        slotProps={{ root: { style: { maxWidth: '540px', width: '100%' } } }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <MenuContainer>
              <ClickAwayListener onClickAway={handleClose}>
                <BankAccountSelectStyled id="account-select-menu" autoFocusItem>
                  {bankAccounts.map((bankAccount, index) => (
                    <Fragment key={bankAccount.id}>
                      <MenuItemStyled
                        borderless
                        onClick={() => {
                          handleMenuItemClick(bankAccount.id);
                        }}
                      >
                        <Account
                          selected={bankAccount.id === selectedBankAccountId}
                          {...bankAccount}
                        />
                      </MenuItemStyled>
                      {index < bankAccounts.length - 1 ? (
                        <Box
                          sx={(theme) => ({
                            borderBottom: `1px solid ${theme.palette.grey[300]}`,
                          })}
                        />
                      ) : null}
                    </Fragment>
                  ))}
                  <Box />
                  <AddAccount
                    onClick={() => {
                      setOpen(false);
                      onBankAccountAdd(true);
                    }}
                  />
                </BankAccountSelectStyled>
              </ClickAwayListener>
            </MenuContainer>
          </Grow>
        )}
      </StyledPopper>
    </Container>
  );
};

export default BankAccountSelect;
